import { config } from "../config";

// In local development, test using your ngrok url. You also have to
// allow it as a redirect url in Google Console  e.g.:
// const BASE_URL = "https://5440-2605-59c8-22f4-c810-6c65-d9b1-1eba-749b.ngrok-free.app";

const BASE_URL = config.BASE_URL;

export const submitCloudTablesOAuthForm = ({
  email,
  redirectPath,
}: {
  email: string;
  redirectPath: string;
}) => {
  const form = document.createElement("form");

  form.setAttribute("method", "GET");

  form.setAttribute("action", "https://accounts.google.com/o/oauth2/v2/auth");

  const params = {
    client_id: config.GOOGLE_CLIENT_ID,
    redirect_uri: `${BASE_URL}/api/auth/cloud_table_auth_callback`,
    response_type: "code",
    scope: "https://www.googleapis.com/auth/drive",
    state: JSON.stringify({
      redirect_url: `${BASE_URL}${redirectPath}`,
      email,
    }),
    access_type: "offline",
    prompt: "consent",
  };

  for (const p in params) {
    const input = document.createElement("input");

    input.setAttribute("type", "hidden");

    input.setAttribute("name", p);

    input.setAttribute("value", params[p]);

    form.appendChild(input);
  }

  document.body.appendChild(form);

  form.submit();
};

export const submitLoginGoogleOAuthForm = ({ redirectUrl }: { redirectUrl?: string }) => {
  const form = document.createElement("form");

  form.setAttribute("method", "GET");
  form.setAttribute("action", "https://accounts.google.com/o/oauth2/v2/auth");

  // CSRF double-submit token: set a short-lived cookie and echo the same value in
  // `state`. The callback requires the two to match, so a forged/replayed callback
  // (whose attacker can't set this cookie on the victim's browser) can't complete a
  // login. SameSite=Lax so the cookie survives the top-level redirect back from Google.
  const nonce = crypto.randomUUID();
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `g_login_csrf=${nonce}; Max-Age=600; Path=/; SameSite=Lax${secure}`;

  const state: Record<string, string> = { nonce };
  if (redirectUrl) {
    // Key must be "redirect" to match the server's login-completion redirect handling.
    state.redirect = redirectUrl;
  }

  const params: Record<string, string> = {
    client_id: config.GOOGLE_CLIENT_ID,
    redirect_uri: `${BASE_URL}/api/auth/web_dashboard_login_google_oauth_callback`,
    response_type: "code",
    scope: ["openid", "email", "profile"].join(" "),
    state: JSON.stringify(state),
    // Always show the account chooser. Users signed into multiple Google accounts
    // otherwise get silently authenticated as the wrong account and bounced back to
    // the login page (the "login loop"). This is the fix for that.
    prompt: "select_account",
  };

  for (const p in params) {
    const input = document.createElement("input");
    input.setAttribute("type", "hidden");
    input.setAttribute("name", p);
    input.setAttribute("value", params[p]);
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
};

export const submitWebDashboardGoogleOAuthForm = ({
  email,
  redirectPath,
  webDashboardId,
}: {
  email: string;
  redirectPath: string;
  webDashboardId?: string;
}) => {
  const form = document.createElement("form");

  form.setAttribute("method", "GET");
  form.setAttribute("action", "https://accounts.google.com/o/oauth2/v2/auth");

  const params: Record<string, string> = {
    client_id: config.GOOGLE_CLIENT_ID,
    redirect_uri: `${BASE_URL}/api/auth/web_dashboard_google_oauth_callback`,
    response_type: "code",
    // Hint the expected account; still allow choosing.
    login_hint: email,
    scope: [
      "openid",
      "https://www.googleapis.com/auth/userinfo.profile",
      "https://www.googleapis.com/auth/userinfo.email",
      "https://www.googleapis.com/auth/spreadsheets",
    ].join(" "),
    state: JSON.stringify({
      redirect_url: `${BASE_URL}${redirectPath}`,
      email,
      web_dashboard_id: webDashboardId,
    }),
    access_type: "offline",
    // Show account chooser even if Chrome is logged into a personal account.
    prompt: "consent select_account",
  };

  for (const p in params) {
    const input = document.createElement("input");
    input.setAttribute("type", "hidden");
    input.setAttribute("name", p);
    input.setAttribute("value", params[p]);
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
};
